export type PersistedState = {
  version: 2;
  collapsed: string[];
  coordinateSystem: "wgs84" | "eov";
  hintsSeen: string[];
  opacities: Record<string, number>;
  panelPinned: boolean;
  recentSearches: number[];
};

export const storagePrefix = "fortex-map:";

export function emptyPersistedState(): PersistedState {
  return { version: 2, collapsed: [], coordinateSystem: "wgs84", hintsSeen: [], opacities: {}, panelPinned: false, recentSearches: [] };
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((a): a is string => typeof a === "string") : [];
}

export function readPersistedState(storageKey: string | null, storage: Storage | null = typeof window === "undefined" ? null : window.localStorage): PersistedState {
  const state = emptyPersistedState();

  if (storageKey === null || storage === null) return state;

  try {
    const raw = storage.getItem(storagePrefix + storageKey);

    if (raw === null) return state;

    const parsed = JSON.parse(raw) as Partial<PersistedState> | null;

    if (!parsed || typeof parsed !== "object" || parsed.version !== 2) return state;

    state.collapsed = strings(parsed.collapsed);
    state.hintsSeen = strings(parsed.hintsSeen);
    state.coordinateSystem = parsed.coordinateSystem === "eov" ? "eov" : "wgs84";
    state.panelPinned = parsed.panelPinned === true;
    state.recentSearches = Array.isArray(parsed.recentSearches) ? parsed.recentSearches.filter((a): a is number => typeof a === "number") : [];

    if (parsed.opacities && typeof parsed.opacities === "object") {
      for (const [id, value] of Object.entries(parsed.opacities)) {
        if (typeof value === "number" && Number.isFinite(value)) state.opacities[id] = Math.min(1, Math.max(0, value));
      }
    }

    return state;
  } catch {
    return state;
  }
}

export function writePersistedState(storageKey: string | null, state: PersistedState, storage: Storage | null = typeof window === "undefined" ? null : window.localStorage): void {
  if (storageKey === null || storage === null) return;

  try {
    storage.setItem(storagePrefix + storageKey, JSON.stringify(state));
  } catch {
    return;
  }
}
